import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "coverPhoto", "uploadButton", "modal", "fileInput", "preview", 
    "canvas", "cropArea", "saveButton", "cancelButton",
    "zoomSlider", "rotateButton"
  ]
  
  static values = {
    userId: String,
    updateUrl: String
  }
  
  connect() {
    this.imageData = null
    this.cropData = {
      x: 0,
      y: 0,
      width: 1200, // Cover photo aspect ratio
      height: 320,
      scale: 1,
      rotation: 0
    }
    this.isDragging = false
    this.dragStart = { x: 0, y: 0 }
    this.setupEventListeners()
  }
  
  disconnect() {
    this.removeEventListeners()
  }
  
  setupEventListeners() {
    if (this.hasCanvasTarget) {
      this.canvas = this.canvasTarget
      this.ctx = this.canvas.getContext('2d')
      
      // Set cursor style
      this.canvas.style.cursor = 'grab'
      
      this.canvas.addEventListener('mousedown', this.startDrag.bind(this))
      this.canvas.addEventListener('mousemove', this.drag.bind(this))
      this.canvas.addEventListener('mouseup', this.endDrag.bind(this))
      this.canvas.addEventListener('mouseleave', this.endDrag.bind(this))
      this.canvas.addEventListener('wheel', this.handleZoom.bind(this))
    }
  }
  
  removeEventListeners() {
    if (this.canvas) {
      this.canvas.removeEventListener('mousedown', this.startDrag.bind(this))
      this.canvas.removeEventListener('mousemove', this.drag.bind(this))
      this.canvas.removeEventListener('mouseup', this.endDrag.bind(this))
      this.canvas.removeEventListener('mouseleave', this.endDrag.bind(this))
      this.canvas.removeEventListener('wheel', this.handleZoom.bind(this))
    }
  }
  
  openEditor() {
    this.modalTarget.classList.remove('hidden')
    this.modalTarget.classList.add('flex')
    document.body.classList.add('overflow-hidden')
  }
  
  closeEditor() {
    this.modalTarget.classList.add('hidden')
    this.modalTarget.classList.remove('flex')
    document.body.classList.remove('overflow-hidden')
    this.resetEditor()
  }
  
  resetEditor() {
    this.imageData = null
    this.cropData = {
      x: 0,
      y: 0,
      width: 1200,
      height: 320,
      scale: 1,
      rotation: 0
    }
    this.imagePosition = { x: 0, y: 0 }
    this.imageScale = 1
    this.fileInputTarget.value = ''
    if (this.hasZoomSliderTarget) {
      this.zoomSliderTarget.value = 1
    }
  }
  
  handleFileSelect() {
    const file = this.fileInputTarget.files[0]
    if (!file) return
    
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file.')
      return
    }
    
    const reader = new FileReader()
    reader.onload = (e) => {
      this.loadImage(e.target.result)
    }
    reader.readAsDataURL(file)
  }
  
  loadImage(src) {
    const img = new Image()
    img.onload = () => {
      this.imageData = {
        img: img,
        originalWidth: img.width,
        originalHeight: img.height
      }
      this.setupCanvas()
      this.drawImage()
    }
    img.src = src
  }
  
  setupCanvas() {
    // Use cover photo aspect ratio (roughly 3.75:1)
    this.canvas.width = 750
    this.canvas.height = 200
    
    // Cover the entire canvas with crop area
    this.cropData.width = this.canvas.width * 0.9
    this.cropData.height = this.canvas.height * 0.9
    this.cropData.x = (this.canvas.width - this.cropData.width) / 2
    this.cropData.y = (this.canvas.height - this.cropData.height) / 2
    
    // Initialize image position and scale
    this.imagePosition = { x: 0, y: 0 }
    this.imageScale = 1
  }
  
  drawImage() {
    if (!this.imageData) return
    
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height)
    
    // Calculate image dimensions to fit canvas while maintaining aspect ratio
    const { img } = this.imageData
    const canvasAspect = this.canvas.width / this.canvas.height
    const imageAspect = img.width / img.height
    
    let baseWidth, baseHeight
    
    // Cover the canvas area (like CSS background-size: cover)
    if (imageAspect > canvasAspect) {
      // Image is wider - fit to height
      baseHeight = this.canvas.height
      baseWidth = baseHeight * imageAspect
    } else {
      // Image is taller - fit to width  
      baseWidth = this.canvas.width
      baseHeight = baseWidth / imageAspect
    }
    
    // Apply user scaling
    const drawWidth = baseWidth * this.imageScale
    const drawHeight = baseHeight * this.imageScale
    
    // Apply user positioning (centered by default)
    const drawX = (this.canvas.width - drawWidth) / 2 + this.imagePosition.x
    const drawY = (this.canvas.height - drawHeight) / 2 + this.imagePosition.y
    
    // Save context for rotation and high-quality rendering
    this.ctx.save()
    this.ctx.imageSmoothingEnabled = true
    this.ctx.imageSmoothingQuality = 'high'
    
    // Apply rotation around center
    if (this.cropData.rotation !== 0) {
      this.ctx.translate(this.canvas.width / 2, this.canvas.height / 2)
      this.ctx.rotate((this.cropData.rotation * Math.PI) / 180)
      this.ctx.translate(-this.canvas.width / 2, -this.canvas.height / 2)
    }
    
    // Draw the image with high quality
    this.ctx.drawImage(img, drawX, drawY, drawWidth, drawHeight)
    
    // Restore context
    this.ctx.restore()
    
    // Draw crop overlay
    this.drawCropOverlay()
  }
  
  drawCropOverlay() {
    this.ctx.save()
    
    // Draw subtle overlay outside crop area
    this.ctx.fillStyle = 'rgba(0, 0, 0, 0.3)'
    this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height)
    
    // Clear the crop area (make it transparent)
    this.ctx.globalCompositeOperation = 'destination-out'
    this.ctx.fillRect(
      this.cropData.x,
      this.cropData.y,
      this.cropData.width,
      this.cropData.height
    )
    
    // Draw clean crop rectangle border
    this.ctx.globalCompositeOperation = 'source-over'
    this.ctx.strokeStyle = '#FFFFFF'
    this.ctx.lineWidth = 3
    this.ctx.shadowColor = 'rgba(0, 0, 0, 0.3)'
    this.ctx.shadowBlur = 6
    this.ctx.strokeRect(
      this.cropData.x,
      this.cropData.y,
      this.cropData.width,
      this.cropData.height
    )
    
    // Add inner border for clarity
    this.ctx.shadowBlur = 0
    this.ctx.strokeStyle = 'rgba(59, 130, 246, 0.8)'
    this.ctx.lineWidth = 1
    this.ctx.strokeRect(
      this.cropData.x + 1,
      this.cropData.y + 1,
      this.cropData.width - 2,
      this.cropData.height - 2
    )
    
    this.ctx.restore()
  }
  
  startDrag(event) {
    const rect = this.canvas.getBoundingClientRect()
    const x = event.clientX - rect.left
    const y = event.clientY - rect.top
    
    // Always allow dragging anywhere on the canvas - drag the image, not the crop area
    this.isDragging = true
    this.dragStart = { x: x - this.imagePosition.x, y: y - this.imagePosition.y }
    this.canvas.style.cursor = 'grabbing'
    event.preventDefault()
  }
  
  drag(event) {
    if (!this.isDragging) return
    
    const rect = this.canvas.getBoundingClientRect()
    const x = event.clientX - rect.left
    const y = event.clientY - rect.top
    
    // Update image position - drag the image, not the crop area
    this.imagePosition.x = x - this.dragStart.x
    this.imagePosition.y = y - this.dragStart.y
    
    this.drawImage()
    event.preventDefault()
  }
  
  endDrag() {
    this.isDragging = false
    this.canvas.style.cursor = 'grab'
  }
  
  handleZoom(event) {
    event.preventDefault()
    const delta = event.deltaY > 0 ? -0.1 : 0.1
    this.imageScale = Math.max(0.5, Math.min(3, this.imageScale + delta))
    
    if (this.hasZoomSliderTarget) {
      this.zoomSliderTarget.value = this.imageScale
    }
    
    this.drawImage()
  }
  
  updateZoom() {
    if (this.hasZoomSliderTarget) {
      this.imageScale = parseFloat(this.zoomSliderTarget.value)
      this.drawImage()
    }
  }
  
  rotateImage() {
    this.cropData.rotation = (this.cropData.rotation + 90) % 360
    this.drawImage()
  }
  
  async saveCoverPhoto() {
    if (!this.imageData) return
    
    try {
      this.saveButtonTarget.disabled = true
      this.saveButtonTarget.innerHTML = '<i class="fas fa-spinner fa-spin mr-2"></i>Saving...'
      
      // Create high-quality cropped image
      const croppedCanvas = document.createElement('canvas')
      const outputWidth = 1200  // Cover photo width
      const outputHeight = 320 // Cover photo height  
      croppedCanvas.width = outputWidth
      croppedCanvas.height = outputHeight
      const croppedCtx = croppedCanvas.getContext('2d')
      
      // Enable high-quality rendering
      croppedCtx.imageSmoothingEnabled = true
      croppedCtx.imageSmoothingQuality = 'high'
      
      // Calculate the crop area in canvas coordinates
      const sourceX = this.cropData.x
      const sourceY = this.cropData.y
      const sourceWidth = this.cropData.width
      const sourceHeight = this.cropData.height
      
      // Draw the cropped portion at high resolution
      croppedCtx.drawImage(
        this.canvas,
        sourceX, sourceY, sourceWidth, sourceHeight,
        0, 0, outputWidth, outputHeight
      )
      
      // Convert to blob
      const blob = await new Promise(resolve => {
        croppedCanvas.toBlob(resolve, 'image/jpeg', 0.9)
      })
      
      // Upload via FormData
      const formData = new FormData()
      formData.append('user[cover_photo]', blob, 'cover_photo.jpg')
      formData.append('authenticity_token', this.getCSRFToken())
      
      const response = await fetch(this.updateUrlValue, {
        method: 'PATCH',
        headers: {
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: formData
      })
      
      if (response.ok) {
        const data = await response.json()
        if (data.success) {
          // Update the cover photo in the DOM
          if (data.cover_photo_url) {
            // Find the cover photo container
            const coverContainer = document.querySelector('.cover-photo-container')
            if (coverContainer) {
              // Update existing image or replace placeholder
              let coverImg = coverContainer.querySelector('img')
              if (coverImg) {
                coverImg.src = data.cover_photo_url + '?t=' + Date.now()
              } else {
                // Create new image element
                coverImg = document.createElement('img')
                coverImg.src = data.cover_photo_url + '?t=' + Date.now()
                coverImg.className = 'w-full h-64 object-cover'
                coverImg.alt = 'Cover photo'
                coverImg.style.filter = 'brightness(0.7)'
                
                // Replace the gradient background
                coverContainer.innerHTML = ''
                coverContainer.appendChild(coverImg)
                coverContainer.classList.remove('bg-gradient-to-r', 'from-blue-600', 'to-purple-600')
                coverContainer.classList.add('bg-gray-900')
              }
            }
          }
          
          this.closeEditor()
          this.showNotification('Cover photo updated successfully!', 'success')
        } else {
          throw new Error(data.error || 'Failed to update cover photo')
        }
      } else {
        throw new Error(`HTTP ${response.status}`)
      }
      
    } catch (error) {
      console.error('Cover photo update error:', error)
      this.showNotification('Failed to update cover photo. Please try again.', 'error')
    } finally {
      this.saveButtonTarget.disabled = false
      this.saveButtonTarget.innerHTML = '<i class="fas fa-save mr-2"></i>Save Cover Photo'
    }
  }
  
  getCSRFToken() {
    const token = document.querySelector('meta[name="csrf-token"]')
    return token ? token.getAttribute('content') : ''
  }
  
  showNotification(message, type = 'info') {
    // Simple notification - you can enhance this with a proper notification system
    const notification = document.createElement('div')
    notification.className = `fixed top-4 right-4 z-50 px-4 py-2 rounded-lg text-white ${
      type === 'success' ? 'bg-green-500' : 
      type === 'error' ? 'bg-red-500' : 'bg-blue-500'
    }`
    notification.textContent = message
    
    document.body.appendChild(notification)
    
    setTimeout(() => {
      notification.remove()
    }, 3000)
  }
}